import { useMemo, useState, useEffect } from "react";
import { UC, SC, SL, pmCellStatus, getDuePMs, can } from "../config";
import { apiCall } from "../api";
import { CalculationTrail } from "./common/CalculationTrail";
import {
  Boxes, CalendarClock, AlertTriangle, PackageX, ClipboardList, Gauge,
  Activity, Bell, Layers, CircleCheck, Timer, Wrench,
} from "lucide-react";

// Dense operations-console palette. Written out in full — Tailwind only ships
// class names it can see as complete strings.
const TONES = {
  blue:   { chip: "bg-blue-50 text-blue-600 ring-blue-100",       value: "text-blue-700",    bar: "bg-blue-500" },
  orange: { chip: "bg-orange-50 text-orange-600 ring-orange-100", value: "text-orange-700",  bar: "bg-orange-500" },
  red:    { chip: "bg-red-50 text-red-600 ring-red-100",          value: "text-red-700",     bar: "bg-red-500" },
  green:  { chip: "bg-green-50 text-green-600 ring-green-100",    value: "text-green-700",   bar: "bg-green-500" },
  purple: { chip: "bg-purple-50 text-purple-600 ring-purple-100", value: "text-purple-700",  bar: "bg-purple-500" },
  teal:   { chip: "bg-teal-50 text-teal-600 ring-teal-100",       value: "text-teal-700",    bar: "bg-teal-500" },
};

function StatTile({ icon: Icon, label, value, sub, tone = "blue", testId }) {
  const t = TONES[tone];
  return (
    <div
      className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white px-2.5 py-2 shadow-sm transition hover:border-slate-300 hover:shadow"
      data-testid={testId}
    >
      <div className={`absolute inset-y-0 left-0 w-[3px] ${t.bar}`} />
      <div className="flex items-start gap-2 pl-1.5">
        <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md ring-1 ${t.chip}`}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <div className={`text-xl font-bold leading-none tabular-nums ${t.value}`}>{value}</div>
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 truncate">{label}</div>
          <div className="text-[10px] leading-tight text-slate-400 truncate" title={sub}>{sub}</div>
        </div>
      </div>
    </div>
  );
}

// Hours, or an em dash when there is nothing to average — "0" would read as "instant".
function fmtH(v) {
  return (v === null || v === undefined) ? "\u2014" : `${v} h`;
}

function MiniStat({ label, value, sub, tone = "blue", testId }) {
  const t = TONES[tone];
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-bold leading-tight ${t.value}`} data-testid={`${testId}-value`}>{value}</div>
      <div className="text-[10px] text-slate-400">{sub}</div>
    </div>
  );
}

function Panel({ icon: Icon, title, right, children, className = "", bodyClass = "p-2.5" }) {
  return (
    <div className={`flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-2.5 py-1.5">
        <h3 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
          {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" strokeWidth={2.2} />}
          {title}
        </h3>
        {right}
      </div>
      <div className={`flex-1 ${bodyClass}`}>{children}</div>
    </div>
  );
}

const SEG = {
  done:      { color: "#10b981", dot: "bg-emerald-500", label: "Completed" },
  delayed:   { color: "#f59e0b", dot: "bg-amber-500",   label: "Delayed" },
  pending:   { color: "#3b82f6", dot: "bg-blue-500",    label: "Pending" },
  cancelled: { color: "#f43f5e", dot: "bg-rose-500",    label: "Cancelled" },
};

// Donut drawn with a conic-gradient — no chart library, no extra bundle weight.
function Donut({ segments, size = 108, thickness = 13, centre, caption }) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  let acc = 0;
  const stops = segments
    .filter(s => s.value > 0)
    .map(s => {
      const from = (acc / total) * 100;
      acc += s.value;
      return `${s.color} ${from}% ${(acc / total) * 100}%`;
    })
    .join(", ");
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div
        className="h-full w-full rounded-full"
        style={{ background: total > 0 ? `conic-gradient(${stops})` : "conic-gradient(#e2e8f0 0% 100%)" }}
      />
      <div
        className="absolute flex flex-col items-center justify-center rounded-full bg-white shadow-inner"
        style={{ inset: thickness }}
      >
        <span className="text-lg font-bold leading-none tabular-nums text-slate-800">{centre}</span>
        <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400">{caption}</span>
      </div>
    </div>
  );
}

function SegBar({ stats, testId }) {
  const t = stats.pmTotal || 1;
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100" data-testid={testId}>
      {stats.pmDone > 0 && <div className="h-full bg-emerald-500" style={{ width: `${(stats.pmDone / t) * 100}%` }} />}
      {stats.pmDelayed > 0 && <div className="h-full bg-amber-500" style={{ width: `${(stats.pmDelayed / t) * 100}%` }} />}
      {stats.pmPending > 0 && <div className="h-full bg-blue-500" style={{ width: `${(stats.pmPending / t) * 100}%` }} />}
      {stats.pmCancelled > 0 && <div className="h-full bg-rose-500" style={{ width: `${(stats.pmCancelled / t) * 100}%` }} />}
    </div>
  );
}

function LegendRow({ k, value, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-1.5 rounded border border-slate-100 bg-slate-50/60 px-1.5 py-1">
      <span className={`h-2 w-2 shrink-0 rounded-full ${SEG[k].dot}`} />
      <span className="truncate text-[10px] font-medium text-slate-500">{SEG[k].label}</span>
      <span className="ml-auto text-[11px] font-bold tabular-nums text-slate-700">{value}</span>
      <span className="text-[9px] tabular-nums text-slate-400">{pct}%</span>
    </div>
  );
}

export default function Dashboard({ unitMachines, unitPM, unitSpares, historyLogs, unit, onOpenHistory, user, unitId }) {
  // Reliability for the month so far. Fetched rather than worked out here: the server
  // owns the shift model, the working-day calendar and the in-service rule, and a second
  // implementation on the client is a second set of numbers to go wrong.
  const [rel, setRel] = useState(null);
  const [relErr, setRelErr] = useState("");
  const [showWorking, setShowWorking] = useState(false);
  const canSeeReliability = can(user, "accessReports");
  useEffect(() => {
    if (!canSeeReliability || !unitId) { setRel(null); return; }
    let cancelled = false;
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString("en-CA");
    const to = now.toLocaleDateString("en-CA");
    (async () => {
      try {
        const d = await apiCall("GET", `/reports/reliability/${unitId}?fromDate=${from}&toDate=${to}`);
        if (!cancelled) { setRel(d); setRelErr(""); }
      } catch (e) {
        if (!cancelled) { setRel(null); setRelErr("Could not load reliability figures."); }
      }
    })();
    return () => { cancelled = true; };
  }, [unitId, canSeeReliability]);
  const total = (unitMachines || []).length;
  const depts = (unitMachines || []).reduce((a, m) => { a[m.dept] = (a[m.dept] || 0) + 1; return a; }, {});

  // Dynamic current month
  const now = new Date();
  const monthNames = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const curMonth = `${monthNames[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;

  const currentFY = (() => {
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    return month >= 4 ? `${year}-${String(year + 1).slice(-2)}` : `${year - 1}-${String(year).slice(-2)}`;
  })();
  const currentFYPM = (unitPM || []).filter(p => (p.fy || "2025-26") === currentFY);
  // Due = planned for this month PLUS anything from earlier months of this FY that was
  // never completed. An unfinished PM keeps showing until it is closed; its scheduled
  // month in the PM Plan never changes.
  const duePM = useMemo(() => getDuePMs(currentFYPM, curMonth, currentFY), [currentFYPM, curMonth, currentFY]);
  const overduePM = duePM.filter(d => d.carriedForward);
  const lowStock = (unitSpares || []).filter(s => s.availQty <= s.reorderLevel);
  const totalInc = (historyLogs || []).length;
  const accent = UC[unit?.color] || UC.blue;

  // PM Completion Rate (current FY)
  const pmStats = useMemo(() => {
    const allStatuses = currentFYPM.flatMap(p => Object.values(p.schedule || {}).map(v => typeof v === "object" ? v.status : v));
    const pmDone = allStatuses.filter(s => s === "DONE").length;
    const pmDelayed = allStatuses.filter(s => s === "DELAYED").length;
    const pmCancelled = allStatuses.filter(s => s === "CANCELLED").length;
    const pmPending = allStatuses.filter(s => s === "PLAN").length;
    const pmTotal = pmDone + pmDelayed + pmCancelled + pmPending;
    return { pmDone, pmDelayed, pmCancelled, pmPending, pmTotal };
  }, [currentFYPM]);

  // PM Completion Rate (monthly) — user-selectable month dropdown.
  // Completed/Delayed scoped by completedDate falling in the selected month.
  // Pending/Cancelled scoped by the schedule cell's month-key matching the selected month.
  const [selectedMonth, setSelectedMonth] = useState(curMonth);

  const availableMonths = useMemo(() => {
    const set = new Set();
    (unitPM || []).forEach(p => {
      Object.keys(p.schedule || {}).forEach(k => set.add(k));
      Object.values(p.schedule || {}).forEach(cell => {
        if (cell && typeof cell === "object" && cell.completedDate) {
          const d = new Date(cell.completedDate);
          if (!isNaN(d)) set.add(`${monthNames[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`);
        }
      });
    });
    set.add(curMonth);
    return Array.from(set).sort((a, b) => {
      const [ma, ya] = a.split("-");
      const [mb, yb] = b.split("-");
      const ay = parseInt(ya, 10), by = parseInt(yb, 10);
      if (ay !== by) return by - ay;
      return monthNames.indexOf(mb) - monthNames.indexOf(ma);
    });
  }, [unitPM, curMonth]);

  const monthlyStats = useMemo(() => {
    const dateInMonth = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (isNaN(d)) return false;
      return `${monthNames[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}` === selectedMonth;
    };
    let pmDone = 0, pmDelayed = 0, pmPending = 0, pmCancelled = 0;
    for (const plan of unitPM) {
      const sched = plan.schedule || {};
      for (const [monthKey, cell] of Object.entries(sched)) {
        if (cell == null) continue;
        // Cells can be plain strings ("PLAN", "CANCELLED", ...) or objects ({status, completedDate, ...}).
        // The existing FY widget handles both forms; the previous monthly logic missed the string form.
        const isObj = typeof cell === "object";
        const status = isObj ? cell.status : cell;
        const completedDate = isObj ? cell.completedDate : null;
        if (status === "DONE" && dateInMonth(completedDate)) pmDone++;
        else if (status === "DELAYED" && dateInMonth(completedDate)) pmDelayed++;
        else if (status === "PLAN" && monthKey === selectedMonth) pmPending++;
        else if (status === "CANCELLED" && monthKey === selectedMonth) pmCancelled++;
      }
    }
    const pmTotal = pmDone + pmDelayed + pmPending + pmCancelled;
    return { pmDone, pmDelayed, pmPending, pmCancelled, pmTotal };
  }, [unitPM, selectedMonth]);

  // Recent Maintenance Activity (last 10)
  const recentActivity = useMemo(() => {
    return [...(historyLogs || [])]
      .sort((a, b) => (b.createdAt || b.startDate || "").localeCompare(a.createdAt || a.startDate || ""))
      .slice(0, 10);
  }, [historyLogs]);

  const monthlyPct = monthlyStats.pmTotal > 0 ? Math.round((monthlyStats.pmDone / monthlyStats.pmTotal) * 100) : 0;
  const fyPct = pmStats.pmTotal > 0 ? Math.round((pmStats.pmDone / pmStats.pmTotal) * 100) : 0;
  const deptRows = Object.entries(depts).sort((a, b) => b[1] - a[1]);
  const maxDept = deptRows.length ? deptRows[0][1] : 1;

  return (
    <div className="space-y-2.5 text-slate-800" data-testid="dashboard">
      {/* Unit strip */}
      <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1 overflow-hidden rounded-lg border border-slate-200 bg-gradient-to-r from-white via-white to-slate-50 py-2 pl-4 pr-3 shadow-sm">
        <div className={`absolute inset-y-0 left-0 w-1 ${accent}`} />
        <div className="text-sm font-bold leading-tight">{unit?.name}</div>
        <span className="hidden h-3 w-px bg-slate-200 sm:block" />
        <div className="truncate text-[11px] text-slate-500">{unit?.address}</div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">MNT-F-01</span>
          <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">FY {currentFY}</span>
          <span className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700">{curMonth}</span>
          <span className="text-[10px] text-slate-400">Master: {unit?.lastUpdated}</span>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        <StatTile icon={Boxes} label="Total Machines" value={total} sub="Active fleet" tone="blue" testId="stat-total-machines" />
        <StatTile icon={CalendarClock} label="PM Due" value={duePM.length} sub={overduePM.length > 0 ? `${curMonth} + ${overduePM.length} overdue` : curMonth} tone={overduePM.length > 0 ? "red" : "orange"} testId="stat-pm-due" />
        <StatTile icon={AlertTriangle} label="Overdue PM" value={overduePM.length} sub={overduePM.length > 0 ? "Carried forward" : "Nothing carried over"} tone={overduePM.length > 0 ? "red" : "green"} testId="stat-overdue-pm" />
        <StatTile icon={PackageX} label="Low Stock Parts" value={lowStock.length} sub="At/below reorder" tone={lowStock.length > 0 ? "red" : "green"} testId="stat-low-stock-parts" />
        <StatTile icon={ClipboardList} label="Incidents Logged" value={totalInc} sub="All machines" tone="purple" testId="stat-incidents-logged" />
        <StatTile icon={Gauge} label="PM Compliance" value={`${fyPct}%`} sub={`FY ${currentFY} · ${pmStats.pmDone}/${pmStats.pmTotal} done`} tone={fyPct >= 80 ? "green" : fyPct >= 50 ? "teal" : "orange"} testId="stat-pm-compliance" />
      </div>

      {/* Reliability — the two figures people quote, with their working shown so nobody
          has to take them on trust. Hidden from roles without Reports access, which is
          what the endpoint itself enforces. */}
      {canSeeReliability && (
        <Panel
          icon={Activity}
          title="Reliability this month"
          right={<span className="text-[10px] text-slate-400" data-testid="rel-period">{`1-${curMonth} to today`}</span>}
        >
          {relErr ? (
            <div className="text-xs text-slate-400" data-testid="dash-rel-error">{relErr}</div>
          ) : !rel ? (
            <div className="text-xs text-slate-400" data-testid="dash-rel-loading">Calculating…</div>
          ) : (
            <div className="space-y-2" data-testid="dash-reliability">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {/* MTBF is per machine already — operating hours are machine-hours. The
                    line under it is the different question: how long the unit runs
                    before anything at all stops. */}
                <MiniStat label="MTBF" value={fmtH(rel.summary.mtbfHours)}
                  sub={rel.summary.hoursBetweenBreakdowns != null
                    ? `one machine · unit stops every ${rel.summary.hoursBetweenBreakdowns} h`
                    : "one machine, between its own failures"}
                  tone="blue" testId="dash-mtbf" />
                <MiniStat label="MTTR (completed)" value={fmtH(rel.summary.closedMttrHours)} sub="real repair time" tone="green" testId="dash-mttr-closed" />
                <MiniStat label="MTTR (all)" value={fmtH(rel.summary.mttrHours)} sub={rel.summary.openCount ? `${rel.summary.openCount} still open` : "nothing open"} tone={rel.summary.openCount ? "orange" : "teal"} testId="dash-mttr" />
                <MiniStat label="Breakdowns" value={rel.summary.failures || 0} sub={`${fmtH(rel.summary.downtimeHours)} lost`} tone={rel.summary.failures ? "red" : "green"} testId="dash-failures" />
              </div>
              {!!rel.summary.openCount && (
                <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid="dash-rel-open-warning">
                  {fmtH(rel.summary.openDowntimeHours)} of that comes from {rel.summary.openCount}{" "}
                  {rel.summary.openCount === 1 ? "entry" : "entries"} nobody has closed, so <strong>MTTR (completed)</strong> is the figure to trust.
                </p>
              )}
              {rel.summary.shiftLabel && (
                <p className={`text-[11px] ${rel.summary.shiftSource === "unit" ? "text-slate-500" : "text-amber-700"}`} data-testid="dash-shift-basis">
                  Working day: {rel.summary.shiftLabel}
                </p>
              )}
              <CalculationTrail
                steps={rel.calculation}
                open={showWorking}
                onToggle={() => setShowWorking(v => !v)}
                testId="dash-calc"
              />
            </div>
          )}
        </Panel>
      )}

      {/* Row 1 — compliance · departments · PM due */}
      <div className="grid gap-2.5 lg:grid-cols-12">
        <Panel
          icon={Gauge}
          title="PM Completion"
          className="lg:col-span-4"
          right={
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 outline-none focus:border-blue-400"
              data-testid="pm-monthly-month-picker"
            >
              {availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          }
        >
          {monthlyStats.pmTotal > 0 ? (
            <div className="flex items-center gap-3">
              <Donut
                centre={`${monthlyPct}%`}
                caption="done"
                segments={[
                  { value: monthlyStats.pmDone, color: SEG.done.color },
                  { value: monthlyStats.pmDelayed, color: SEG.delayed.color },
                  { value: monthlyStats.pmPending, color: SEG.pending.color },
                  { value: monthlyStats.pmCancelled, color: SEG.cancelled.color },
                ]}
              />
              <div className="grid min-w-0 flex-1 grid-cols-2 gap-1">
                <LegendRow k="done" value={monthlyStats.pmDone} total={monthlyStats.pmTotal} />
                <LegendRow k="delayed" value={monthlyStats.pmDelayed} total={monthlyStats.pmTotal} />
                <LegendRow k="pending" value={monthlyStats.pmPending} total={monthlyStats.pmTotal} />
                <LegendRow k="cancelled" value={monthlyStats.pmCancelled} total={monthlyStats.pmTotal} />
              </div>
            </div>
          ) : (
            <div className="flex h-[108px] items-center justify-center text-[11px] text-slate-400">
              No PM activity for {selectedMonth}
            </div>
          )}
          <div className="mt-2 border-t border-slate-100 pt-2">
            <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              <span>Current FY {currentFY}</span>
              <span className="tabular-nums text-slate-700">{pmStats.pmDone}/{pmStats.pmTotal} &middot; {fyPct}%</span>
            </div>
            <SegBar stats={pmStats} testId="pm-completion-bar" />
            <div className="mt-2">
              <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                <span>{selectedMonth}</span>
                <span className="tabular-nums text-slate-700">{monthlyStats.pmDone}/{monthlyStats.pmTotal} &middot; {monthlyPct}%</span>
              </div>
              <SegBar stats={monthlyStats} testId="pm-monthly-bar" />
            </div>
          </div>
        </Panel>

        <Panel
          icon={Layers}
          title="Machines by Department"
          className="lg:col-span-4"
          right={<span className="text-[10px] font-semibold text-slate-400">{deptRows.length} depts &middot; {total}</span>}
        >
          <div className="max-h-[232px] space-y-1.5 overflow-y-auto pr-1">
            {deptRows.map(([d, c]) => (
              <div key={d || "—"} className="group flex items-center gap-2">
                <div className="w-24 shrink-0 truncate text-[10px] font-medium text-slate-600" title={d || "Unassigned"}>
                  {d || "Unassigned"}
                </div>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all"
                    style={{ width: `${Math.max((c / maxDept) * 100, 3)}%` }}
                  />
                </div>
                <div className="w-7 shrink-0 text-right text-[11px] font-bold tabular-nums text-slate-700">{c}</div>
                <div className="w-8 shrink-0 text-right text-[9px] tabular-nums text-slate-400">
                  {total > 0 ? Math.round((c / total) * 100) : 0}%
                </div>
              </div>
            ))}
            {deptRows.length === 0 && <div className="py-6 text-center text-[11px] text-slate-400">No machines yet.</div>}
          </div>
        </Panel>

        <Panel
          icon={CalendarClock}
          title={`PM Due — ${curMonth}`}
          className="lg:col-span-4"
          right={
            overduePM.length > 0
              ? <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600 ring-1 ring-red-100">{overduePM.length} overdue</span>
              : <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-bold text-green-600 ring-1 ring-green-100">On schedule</span>
          }
        >
          {duePM.length === 0 ? (
            <div className="flex h-[150px] flex-col items-center justify-center gap-1 text-[11px] text-slate-400">
              <CircleCheck className="h-5 w-5 text-green-400" />
              No PM scheduled this month.
            </div>
          ) : (
            <div className="max-h-[232px] divide-y divide-slate-100 overflow-y-auto pr-1" data-testid="pm-due-list">
              {duePM.map(d => (
                <div
                  key={`${d.plan.id}-${d.month}`}
                  className="flex items-center justify-between gap-2 py-1.5 first:pt-0"
                  data-testid={`pm-due-${d.plan.id}`}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${d.carriedForward ? "bg-red-500" : "bg-blue-400"}`} />
                    <div className="min-w-0">
                      <div className="truncate text-[11px] font-semibold text-slate-700">{d.plan.desc}</div>
                      <div className="text-[10px] text-slate-400">{d.plan.machineCode}</div>
                    </div>
                  </div>
                  {d.carriedForward ? (
                    <span
                      className="shrink-0 whitespace-nowrap rounded-full bg-red-50 px-1.5 py-0.5 text-[9px] font-bold text-red-600 ring-1 ring-red-100"
                      title={`Was due ${d.month} — ${d.monthsLate} month${d.monthsLate === 1 ? "" : "s"} overdue`}
                    >
                      OVERDUE &middot; {d.month}
                    </span>
                  ) : (
                    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${SC[pmCellStatus(d.plan.schedule[d.month])] || "bg-slate-100 text-slate-600"}`}>
                      {SL[pmCellStatus(d.plan.schedule[d.month])] || pmCellStatus(d.plan.schedule[d.month])}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Row 2 — activity · stock */}
      <div className="grid gap-2.5 lg:grid-cols-12">
        <Panel
          icon={Activity}
          title="Recent Maintenance Activity"
          className="lg:col-span-7"
          right={<span className="text-[10px] font-semibold text-slate-400">last {recentActivity.length}</span>}
        >
          {recentActivity.length === 0 ? (
            <div className="py-6 text-center text-[11px] text-slate-400">No activity logged yet.</div>
          ) : (
            <div className="max-h-[236px] divide-y divide-slate-100 overflow-y-auto pr-1">
              {recentActivity.map(log => {
                const machine = (unitMachines || []).find(m => m.id === log.machineId) || (unitMachines || []).find(m => m.code === log.machineCode || m.machineNo === log.machineCode);
                const canNavigate = !!machine && typeof onOpenHistory === "function";
                const badgeBase = "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold whitespace-nowrap";
                const badgeColour = log.workType === "external" ? "bg-orange-50 text-orange-600 ring-1 ring-orange-100" : "bg-blue-50 text-blue-600 ring-1 ring-blue-100";
                const badgeHover = log.workType === "external" ? "hover:bg-orange-100" : "hover:bg-blue-100";
                return (
                  <div key={log.id} className="flex items-center gap-2 py-1.5 first:pt-0">
                    <div className="w-[68px] shrink-0 text-[10px] font-medium tabular-nums text-slate-400">{log.startDate}</div>
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-[11px] font-semibold text-slate-700">{log.description}</span>
                      <span className="text-[10px] text-slate-400">{log.machineCode || "—"}</span>
                    </div>
                    {canNavigate ? (
                      <button
                        type="button"
                        onClick={() => onOpenHistory(machine, log.id)}
                        className={`${badgeBase} ${badgeColour} ${badgeHover} cursor-pointer transition`}
                        data-testid={`recent-activity-badge-${log.id}`}
                        title="Open in History Card"
                      >
                        {log.workType === "external" ? "EXT" : "INT"}
                      </button>
                    ) : (
                      <span className={`${badgeBase} ${badgeColour}`}>{log.workType === "external" ? "EXT" : "INT"}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel
          icon={PackageX}
          title="Low Stock Alert"
          className="lg:col-span-5"
          right={
            lowStock.length > 0
              ? <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600 ring-1 ring-red-100">{lowStock.length} parts</span>
              : <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-bold text-green-600 ring-1 ring-green-100">OK</span>
          }
        >
          {lowStock.length === 0 ? (
            <div className="flex h-[88px] flex-col items-center justify-center gap-1 text-[11px] text-slate-400">
              <CircleCheck className="h-5 w-5 text-green-400" />
              All parts are above reorder level.
            </div>
          ) : (
            <div className="grid max-h-[236px] gap-1 overflow-y-auto pr-1 sm:grid-cols-2">
              {lowStock.map(s => (
                <div key={s.id} className="rounded border border-red-100 bg-red-50/60 px-1.5 py-1">
                  <div className="truncate text-[10px] font-semibold text-red-800" title={s.description}>{s.description}</div>
                  <div className="text-[10px] tabular-nums text-red-500">
                    Avail <strong className="text-red-700">{s.availQty}</strong> / Reorder {s.reorderLevel}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Reminders — one compact strip */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-amber-200 bg-amber-50/70 px-2.5 py-1.5 text-[10px] text-amber-800">
        <span className="flex items-center gap-1 font-bold uppercase tracking-wide text-amber-700">
          <Bell className="h-3 w-3" strokeWidth={2.4} /> Reminders
        </span>
        <span>Master Machine List: <strong>Every April &amp; October</strong></span>
        <span>Spare Parts list: <strong>last date of every month</strong></span>
        <span className="text-amber-600">Docs: MNT-F-01 &middot; MNT-F-03 &middot; MNT-F-04 &middot; MNT-F-05 &middot; MNT-F-11</span>
      </div>
    </div>
  );
}
